import type { ContadorBuzonDTO, NotificacionBuzonDTO } from '../../services/buzonService';
import { formatearFechaBuzon, textoAvisosSinDescartar, textoTareasPorHacer } from './buzonPresentacion';
import { normalizarClaveCategoria } from './categoriasBuzon';
import { normalizarTipoEntidad } from './enlaceNotificacion';

/*
 * Reglas del "buzón inteligente" del panel de la campanita. Son reglas fijas en el front, sin IA, con
 * lo que el SSO ya manda: Accionable, ClaveCategoria, FechaCreacion, TipoEntidad + IdEntidad y la hora
 * de la última visita que guarda el navegador. Todo es puro: el momento de referencia y la última
 * visita llegan como parámetros para poder probarlo. Las fechas del SSO llegan en UTC con `Z` y se
 * comparan en la hora local del navegador.
 */

/**
 * Días de calendario de espera a partir de los cuales una tarea está atrasada.
 *
 * Se cuentan **días de calendario locales** y no bloques de 24 h: una tarea del martes a las 23:50 ya
 * lleva 2 días el jueves a las 00:05. Así coincide con lo que dice el texto ("lleva 2 días") y con
 * cómo se piensa el trabajo (por día), y no cambia de estado a media mañana según la hora exacta en
 * que llegó.
 */
export const UMBRAL_ATRASO_DIAS = 2;

/** Categoría de los avisos de cancelación de CFDI que se cruzan con las tareas. */
export const CATEGORIA_CANCELACION_CFDI = 'AVISOS_CANCELACION_CFDI';

/** Frase de acción por categoría. Las que no están usan el `Titulo` del SSO. */
const FRASE_ACCION: Partial<Record<string, string>> = {
  PENDIENTES_CONFIRMAR: 'Confirma la factura',
  DEVUELTOS: 'Corrige y reenvía la factura',
  SOLICITUDES_REASIGNACION: 'Responde la solicitud de reasignación',
};

const MS_POR_DIA = 86_400_000;

const LOCALE = 'es-MX';

const plural = (n: number, uno: string, varios: string): string => `${n} ${n === 1 ? uno : varios}`;

/** "Hace 40 min" → "hace 40 min", para usar la fecha del buzón a media frase. */
const minusculaInicial = (texto: string): string => texto.charAt(0).toLocaleLowerCase(LOCALE) + texto.slice(1);

/** "entre tus 2 notificaciones…" → "Entre tus 2 notificaciones…", para empezar una frase. */
const mayusculaAlInicio = (texto: string): string => texto.charAt(0).toLocaleUpperCase(LOCALE) + texto.slice(1);

/**
 * "MARBELLA" o "marbella" → "Marbella"; "ÁNGEL" → "Ángel"; "MARÍA-JOSÉ" → "María-José". Las
 * funciones de locale respetan acentos y eñes.
 */
const mayusculaInicial = (palabra: string): string =>
  palabra
    .toLocaleLowerCase(LOCALE)
    .split('-')
    .map((parte) => parte.charAt(0).toLocaleUpperCase(LOCALE) + parte.slice(1))
    .join('-');

const esDevuelta = (n: Pick<NotificacionBuzonDTO, 'ClaveCategoria'>): boolean =>
  normalizarClaveCategoria(n.ClaveCategoria) === 'DEVUELTOS';

/**
 * @function separarTareasYAvisos
 * @description Separa la página cargada en tareas (`Accionable === true`) y avisos. Lo que no viene
 * marcado como tarea se trata como aviso para que ninguna notificación desaparezca del panel.
 * Conserva el orden del SSO en las dos listas.
 */
export const separarTareasYAvisos = <T extends Pick<NotificacionBuzonDTO, 'Accionable'>>(
  elementos: readonly T[]
): { tareas: T[]; avisos: T[] } => ({
  tareas: elementos.filter((n) => n.Accionable === true),
  avisos: elementos.filter((n) => n.Accionable !== true),
});

/**
 * @function diasDeEspera
 * @description Días de calendario locales entre la fecha (ISO del SSO) y `ahora`. Una fecha un poco
 * en el futuro (reloj del equipo atrasado) cuenta como hoy; una fecha inválida regresa `null`.
 */
export const diasDeEspera = (fechaIso: string, ahora: Date = new Date()): number | null => {
  const fecha = new Date(fechaIso);
  if (Number.isNaN(fecha.getTime())) {
    return null;
  }
  const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // `round` absorbe los días de 23 o 25 h de los cambios de horario.
  return Math.max(0, Math.round((inicioDelDia(ahora) - inicioDelDia(fecha)) / MS_POR_DIA));
};

/**
 * @function estaAtrasada
 * @description Si lleva al menos `UMBRAL_ATRASO_DIAS` días de calendario esperando desde su creación.
 */
export const estaAtrasada = (
  n: Pick<NotificacionBuzonDTO, 'FechaCreacion'>,
  ahora: Date = new Date()
): boolean => (diasDeEspera(n.FechaCreacion, ahora) ?? 0) >= UMBRAL_ATRASO_DIAS;

/** Una fecha inválida cuenta como la más antigua posible, para que quede al final del orden. */
const tiempoCreacion = (n: Pick<NotificacionBuzonDTO, 'FechaCreacion'>): number => {
  const tiempo = new Date(n.FechaCreacion).getTime();
  return Number.isNaN(tiempo) ? Number.NEGATIVE_INFINITY : tiempo;
};

const comparar = (a: number, b: number): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * @function ordenarPorMasReciente
 * @description Ordena tareas de la más reciente a la más antigua por `FechaCreacion`, sin importar
 * si están atrasadas ni su categoría. Con el mismo instante va primero el `Id` mayor y las fechas
 * inválidas van al final. No modifica la lista recibida.
 */
export const ordenarPorMasReciente = <T extends Pick<NotificacionBuzonDTO, 'Id' | 'FechaCreacion'>>(
  tareas: readonly T[]
): T[] =>
  [...tareas].sort((a, b) => comparar(tiempoCreacion(b), tiempoCreacion(a)) || comparar(b.Id, a.Id));

/**
 * @function fraseAccionTarea
 * @description Qué tiene que hacer el usuario, dicho como acción ("Confirma la factura"). Para una
 * categoría sin frase propia usa el `Titulo` del SSO.
 */
export const fraseAccionTarea = (n: Pick<NotificacionBuzonDTO, 'ClaveCategoria' | 'Titulo'>): string =>
  FRASE_ACCION[normalizarClaveCategoria(n.ClaveCategoria)] ?? n.Titulo;

/**
 * @function porQueTarea
 * @description Cuánto lleva esperando la tarea, en una línea: cuántos días lleva si está
 * atrasada ("Lleva 5 días esperando tu respuesta."), o hace cuánto llegó con el mismo estilo que la
 * fecha del buzón ("Llegó hace 40 min.", "Llegó ayer."). Una fecha inválida regresa cadena vacía.
 */
export const porQueTarea = (
  n: Pick<NotificacionBuzonDTO, 'ClaveCategoria' | 'FechaCreacion'>,
  ahora: Date = new Date()
): string => {
  const dias = diasDeEspera(n.FechaCreacion, ahora);
  if (dias === null) {
    return '';
  }
  const devuelta = esDevuelta(n);
  if (dias >= UMBRAL_ATRASO_DIAS) {
    return devuelta
      ? `Te la devolvieron hace ${dias} días y sigue detenida.`
      : `Lleva ${dias} días esperando tu respuesta.`;
  }
  const cuando = dias === 1 ? 'ayer' : minusculaInicial(formatearFechaBuzon(n.FechaCreacion, ahora));
  return devuelta ? `Te la devolvieron ${cuando}.` : `Llegó ${cuando}.`;
};

/** Un aviso de cancelación de CFDI de la misma factura que una tarea pendiente. */
export interface AlertaCancelacionBuzon<T = NotificacionBuzonDTO> {
  /** Identifica la alerta en la sesión: `{IdAviso}:{IdTarea}`. */
  clave: string;
  tarea: T;
  aviso: T;
}

type CamposCruce = Pick<
  NotificacionBuzonDTO,
  'Id' | 'Estado' | 'ClaveCategoria' | 'TipoEntidad' | 'IdEntidad' | 'IdEmpresa'
>;

/**
 * `tipo|empresa|id` de la entidad, o `null` si falta alguno: sin entidad completa no hay con qué
 * cruzar. La empresa va en la llave porque el mismo `IdEntidad` puede existir en dos empresas.
 */
const llaveEntidad = (n: CamposCruce): string | null => {
  const tipo = normalizarTipoEntidad(n.TipoEntidad);
  const empresa = n.IdEmpresa === null || n.IdEmpresa === undefined ? '' : String(n.IdEmpresa).trim();
  const id = String(n.IdEntidad ?? '').trim();
  return tipo && empresa && id ? `${tipo}|${empresa}|${id}` : null;
};

/**
 * @function cruzarCancelacionesCfdi
 * @description Busca avisos pendientes de cancelación de CFDI (`AVISOS_CANCELACION_CFDI`) con la
 * misma entidad (`TipoEntidad` normalizado + `IdEmpresa` + `IdEntidad`) que una tarea pendiente. Un aviso ya leído
 * cuenta mientras siga pendiente. Sin entidad completa nunca hay cruce. Respeta el orden de las tareas
 * recibidas y toma un aviso por tarea.
 */
export const cruzarCancelacionesCfdi = <T extends CamposCruce>(
  tareas: readonly T[],
  avisos: readonly T[]
): AlertaCancelacionBuzon<T>[] => {
  const cancelaciones = avisos.filter(
    (a) =>
      a.Estado === 'Pendiente' &&
      normalizarClaveCategoria(a.ClaveCategoria) === CATEGORIA_CANCELACION_CFDI &&
      llaveEntidad(a) !== null
  );
  return tareas.flatMap((tarea) => {
    const llave = tarea.Estado === 'Pendiente' ? llaveEntidad(tarea) : null;
    const aviso = llave === null ? undefined : cancelaciones.find((a) => llaveEntidad(a) === llave);
    return aviso ? [{ clave: `${aviso.Id}:${tarea.Id}`, tarea, aviso }] : [];
  });
};

/**
 * @function saludoBuzon
 * @description Saludo por la hora local: "Buenos días" de 05:00 a 11:59, "Buenas tardes" de 12:00 a
 * 18:59 y "Buenas noches" el resto. Agrega el primer nombre si viene, con mayúscula inicial aunque
 * el SSO lo mande en mayúsculas ("MARBELLA ASCENCIO" → "Buenos días, Marbella").
 */
export const saludoBuzon = (ahora: Date = new Date(), nombreCompleto?: string | null): string => {
  const hora = ahora.getHours();
  const saludo = hora >= 5 && hora < 12 ? 'Buenos días' : hora >= 12 && hora < 19 ? 'Buenas tardes' : 'Buenas noches';
  const primerNombre = (nombreCompleto ?? '').trim().split(/\s+/)[0];
  return primerNombre ? `${saludo}, ${mayusculaInicial(primerNombre)}` : saludo;
};

/**
 * @function textoUltimaVisita
 * @description Pie del panel: "Tu última visita: hace 12 min" / "ayer, 06:10 p.m." / "12 oct., 10:15
 * a.m.", con la fecha del buzón. Sin última visita conocida regresa `null` y el pie no la menciona.
 */
export const textoUltimaVisita = (ultimaVisita: number | null, ahora: Date = new Date()): string | null => {
  // Una marca fuera del rango de `Date` da una fecha inválida, y `toISOString` lanzaría con ella.
  const fecha = new Date(ultimaVisita ?? Number.NaN);
  if (Number.isNaN(fecha.getTime())) {
    return null;
  }
  const cuando = formatearFechaBuzon(fecha.toISOString(), ahora);
  return cuando ? `Tu última visita: ${minusculaInicial(cuando)}` : null;
};

/**
 * @function contarNuevasDesde
 * @description De `elementos`, cuántas se crearon después de la última visita (marca de tiempo en
 * ms). El panel le pasa los pendientes cargados: las que llegaron y ya se atendieron no cuentan, y
 * con pendientes fuera de la página es un mínimo (ver `alcanceBuzon`). Sin última visita conocida
 * regresa `null`: no se sabe qué es nuevo.
 */
export const contarNuevasDesde = (
  elementos: readonly Pick<NotificacionBuzonDTO, 'FechaCreacion'>[],
  ultimaVisita: number | null
): number | null => {
  if (ultimaVisita === null || !Number.isFinite(ultimaVisita)) {
    return null;
  }
  return elementos.filter((n) => {
    const tiempo = new Date(n.FechaCreacion).getTime();
    return !Number.isNaN(tiempo) && tiempo > ultimaVisita;
  }).length;
};

/** Un paso del plan "Empieza por aquí". */
export interface PasoPlanBuzon<T = NotificacionBuzonDTO> {
  notificacion: T;
  /** Qué hacer ("Confirma la factura"). */
  frase: string;
  /** Cuánto lleva esperando o hace cuánto llegó; vacío si no se puede decir. */
  porQue: string;
  atrasada: boolean;
}

/** Qué tanto del buzón trae la página cargada. */
export interface CoberturaPaginaBuzon {
  /**
   * Filas que se usan para el plan, de las más recientes: en `/buzon`, las que trajo la página; en el
   * panel, las visibles (sin las que salen al descartarse).
   */
  cargadas: number;
  /** Pendientes en total según el SSO (`Total` de la página). */
  total: number;
  /**
   * El contador de la campanita (`GET Contador`), si llegó. Sus tareas (`Accionables`) dan el total
   * exacto solo si es la misma foto del buzón que la página (ver `alcanceBuzon`).
   */
  contador?: Pick<ContadorBuzonDTO, 'Pendientes' | 'Accionables'> | null;
  /** La última petición del contador falló: lo que quedó en caché puede ser de otro momento. */
  contadorConError?: boolean;
  /**
   * La página está filtrada por una categoría. El contador (`Accionables`) es de todo el buzón, así
   * que no dice cuántas tareas hay en ella y no se usa.
   */
  conCategoria?: boolean;
}

/** Qué se puede afirmar con lo cargado sin inventar totales. */
export interface AlcanceBuzon {
  /** Hay más pendientes que los cargados. */
  parcial: boolean;
  cargadas: number;
  /** Tareas en total según el contador; `null` si no llegó o no se puede confiar en él. */
  tareasTotales: number | null;
  /** Todas las tareas vinieron en la página: su número, sus atrasos y su orden son exactos. */
  tareasCompletas: boolean;
  /** El número de pendientes nuevas desde la última visita es exacto (vinieron todos los pendientes). */
  nuevasExactas: boolean;
}

/** Sin datos de cobertura se asume que la página trae todo, como cuando cabe completa. */
const ALCANCE_COMPLETO: AlcanceBuzon = {
  parcial: false,
  cargadas: 0,
  tareasTotales: null,
  tareasCompletas: true,
  nuevasExactas: true,
};

/**
 * @function alcanceBuzon
 * @description Decide qué números del resumen son exactos. La página trae las notificaciones más
 * recientes, así que con más pendientes que los cargados:
 * - las tareas solo están completas si el contador (`Accionables`) dice que son las que se ven;
 * - las nuevas (pendientes creadas después de la última visita) son un mínimo: no se supone el orden
 *   de la página, así que solo son exactas si vinieron todos los pendientes.
 *
 * Es la única regla para confiar en el contador; el panel y `/buzon` la usan igual. Sus tareas
 * (`tareasTotales`) solo se toman si el contador es la misma foto del buzón que la página y no la
 * contradice; si no, quedan en `null`:
 * - sin categoría elegida (el contador es de todo el buzón);
 * - sin error en su última petición (lo que quedó en caché puede ser viejo);
 * - con `Pendientes` igual al `total` de la página (mismo alcance: pendientes de todas las categorías);
 * - con al menos las tareas que se ven, y sin más tareas fuera de la página que notificaciones fuera
 *   de ella.
 */
export const alcanceBuzon = (
  { cargadas, total, contador, contadorConError = false, conCategoria = false }: CoberturaPaginaBuzon,
  tareasEnPagina: number,
  nuevas: number | null
): AlcanceBuzon => {
  const parcial = total > cargadas;
  const accionables = contador?.Accionables;
  const confiable =
    !conCategoria &&
    !contadorConError &&
    contador?.Pendientes === total &&
    typeof accionables === 'number' &&
    Number.isFinite(accionables) &&
    accionables >= 0;
  const candidato = confiable ? Math.trunc(accionables) : null;
  const cuadraConLaPagina =
    candidato !== null && candidato >= tareasEnPagina && total - candidato >= cargadas - tareasEnPagina;
  const tareasTotales = cuadraConLaPagina ? candidato : null;
  return {
    parcial,
    cargadas,
    tareasTotales,
    tareasCompletas: !parcial || tareasTotales === tareasEnPagina,
    nuevasExactas: nuevas !== null && !parcial,
  };
};

/** Un número que se puede mostrar: exacto, o solo lo que se vio (un mínimo, "al menos N"). */
export interface ConteoBuzon {
  valor: number;
  exacto: boolean;
}

/** Cuántas tareas y avisos se pueden afirmar. */
export interface ConteosBuzon {
  tareas: ConteoBuzon;
  avisos: ConteoBuzon;
}

/**
 * @function conteosBuzon
 * @description Cuántas tareas y avisos hay, solo con lo que `alcanceBuzon` acepta. Si la página trae
 * todo, se cuenta lo cargado. Si no y el contador es confiable, sus tareas son el total y el resto del
 * total son avisos; si no, solo se puede decir lo que trae la página, como mínimo.
 */
export const conteosBuzon = (cobertura: CoberturaPaginaBuzon, tareasEnPagina: number): ConteosBuzon => {
  const { parcial, tareasTotales } = alcanceBuzon(cobertura, tareasEnPagina, null);
  const avisosEnPagina = Math.max(0, cobertura.cargadas - tareasEnPagina);
  if (!parcial) {
    return { tareas: { valor: tareasEnPagina, exacto: true }, avisos: { valor: avisosEnPagina, exacto: true } };
  }
  if (tareasTotales !== null) {
    return {
      tareas: { valor: tareasTotales, exacto: true },
      avisos: { valor: cobertura.total - tareasTotales, exacto: true },
    };
  }
  return { tareas: { valor: tareasEnPagina, exacto: false }, avisos: { valor: avisosEnPagina, exacto: false } };
};

/**
 * @function resumenEncabezadoBuzon
 * @description La línea del encabezado de `/buzon`, de todo el buzón, con lo que trae la primera
 * página de pendientes (sin categoría). Usa `conteosBuzon`, la misma regla que el panel:
 * - exacto (la página trae todo, o el contador da las tareas): "2 tareas esperan tu acción y tienes
 *   1 aviso sin descartar." / "No tienes tareas por hacer y tienes 173 avisos sin descartar.";
 * - con el contador y tareas fuera de la página: el total exacto y cuántas están entre las más
 *   recientes, entre paréntesis;
 * - sin contador y con la página incompleta: solo habla de "tus N notificaciones más recientes" y
 *   nunca dice "No tienes tareas por hacer".
 * Regresa pedazos de texto plano; el número de tareas va resaltado.
 */
export const resumenEncabezadoBuzon = (cobertura: CoberturaPaginaBuzon, tareasEnPagina: number): FragmentoResumen[] => {
  const { tareas, avisos } = conteosBuzon(cobertura, tareasEnPagina);
  const masRecientes = `tus ${cobertura.cargadas} notificaciones más recientes`;

  if (!tareas.exacto) {
    if (tareas.valor === 0) {
      return [{ texto: `No hay tareas entre ${masRecientes}.`, tono: 'normal' }];
    }
    const resto = avisos.valor === 0 ? '.' : avisos.valor === 1 ? ' y 1 es un aviso.' : ` y ${avisos.valor} son avisos.`;
    return [
      { texto: `Entre ${masRecientes}: `, tono: 'normal' },
      { texto: textoTareasPorHacer(tareas.valor), tono: 'fuerte' },
      { texto: resto, tono: 'normal' },
    ];
  }

  if (tareas.valor === 0 && avisos.valor === 0) {
    return [{ texto: 'Estás al día. Lo que te llegue aparece aquí.', tono: 'normal' }];
  }

  const f: FragmentoResumen[] =
    tareas.valor === 0
      ? [{ texto: 'No tienes tareas por hacer', tono: 'normal' }]
      : [{ texto: textoTareasPorHacer(tareas.valor), tono: 'fuerte' }];
  if (tareas.valor > tareasEnPagina) {
    const entreRecientes =
      tareasEnPagina > 1
        ? `${tareasEnPagina} están entre`
        : tareasEnPagina === 1
          ? '1 está entre'
          : tareas.valor === 1
            ? 'no está entre'
            : 'ninguna está entre';
    f.push({ texto: ` (${entreRecientes} ${masRecientes})`, tono: 'normal' });
  }
  f.push({
    texto: avisos.valor > 0 ? ` y tienes ${textoAvisosSinDescartar(avisos.valor)} sin descartar.` : '.',
    tono: 'normal',
  });
  return f;
};

/** Lo que el panel necesita para pintar el buzón inteligente. */
export interface PlanBuzon<T = NotificacionBuzonDTO> {
  /** Tareas de la más reciente a la más antigua. */
  pasos: PasoPlanBuzon<T>[];
  /** Avisos en el orden del SSO ("Puede esperar"). */
  avisos: T[];
  /** Cruces de cancelación de CFDI, en el orden de los pasos. */
  alertas: AlertaCancelacionBuzon<T>[];
  atrasadas: number;
  /** Nuevas desde la última visita; `null` si no se conoce la última visita. */
  nuevas: number | null;
  /** Qué tanto de lo anterior es exacto. */
  alcance: AlcanceBuzon;
}

/**
 * @function armarPlanBuzon
 * @description Junta todas las reglas sobre la página cargada: separa tareas y avisos, ordena las
 * tareas de la más reciente a la más antigua con su frase y su "por qué", cruza las cancelaciones de
 * CFDI, cuenta atrasadas y nuevas, y dice qué tanto de eso es exacto. Sin `cobertura` asume que la
 * página trae todo.
 */
export const armarPlanBuzon = (
  elementos: readonly NotificacionBuzonDTO[],
  ahora: Date,
  ultimaVisita: number | null,
  cobertura: CoberturaPaginaBuzon = { cargadas: elementos.length, total: elementos.length }
): PlanBuzon => {
  const { tareas, avisos } = separarTareasYAvisos(elementos);
  const ordenadas = ordenarPorMasReciente(tareas);
  const pasos = ordenadas.map((notificacion) => ({
    notificacion,
    frase: fraseAccionTarea(notificacion),
    porQue: porQueTarea(notificacion, ahora),
    atrasada: estaAtrasada(notificacion, ahora),
  }));
  const nuevas = contarNuevasDesde(elementos, ultimaVisita);
  return {
    pasos,
    avisos,
    alertas: cruzarCancelacionesCfdi(ordenadas, avisos),
    atrasadas: pasos.filter((p) => p.atrasada).length,
    nuevas,
    alcance: alcanceBuzon(cobertura, pasos.length, nuevas),
  };
};

/** Cómo se pinta un pedazo del resumen: normal, resaltado o en el tono de lo atrasado. */
export type TonoFragmentoResumen = 'normal' | 'fuerte' | 'urgente';

/** Un pedazo de texto plano del resumen con su tono. */
export interface FragmentoResumen {
  texto: string;
  tono: TonoFragmentoResumen;
}

export interface DatosResumenBuzon {
  /** Pasos del plan, de la tarea más reciente a la más antigua. */
  pasos: readonly Pick<PasoPlanBuzon, 'notificacion' | 'frase' | 'atrasada'>[];
  /** Cuántos avisos quedan. */
  avisos: number;
  /** Nuevas desde la última visita; `null` si no se conoce. */
  nuevas: number | null;
  /** Hay una alerta de cancelación a la vista. */
  hayAlerta: boolean;
  /** Qué tanto es exacto; sin él se asume que la página trae todo. */
  alcance?: AlcanceBuzon;
}

/**
 * El mensaje en una sola línea para ir entre paréntesis, sin su punto final. Quita a lo más un punto
 * y nunca el de una abreviatura o inicial ("S.A. de C.V.", "Proveedor A."): una mayúscula precedida
 * de punto, espacio o el inicio del texto.
 */
const mensajeEnLinea = (mensaje: string | null | undefined): string => {
  const texto = (mensaje ?? '').replace(/\s+/g, ' ').trim();
  if (!texto.endsWith('.') || /(^|[\s.])\p{Lu}\.$/u.test(texto)) {
    return texto;
  }
  return texto.slice(0, -1);
};

/**
 * @function resumenBuzon
 * @description El párrafo con el que el buzón te escribe, armado con plantillas fijas: lo que llegó
 * desde tu última visita, cuántas tareas tienes, cuántas llevan días, por dónde empezar y, si hay una
 * alerta, que la revises antes. Sin tareas dice cuántos avisos quedan; sin nada regresa una lista
 * vacía (el panel muestra "Estás al día"). Regresa pedazos de texto plano con su tono para resaltar
 * sin HTML.
 *
 * Si la página no trae todo (`alcance`), no afirma totales: habla de "tus N notificaciones más
 * recientes", usa el contador de tareas cuando llegó y nunca dice "No tienes tareas por hacer" sin
 * saberlo.
 */
export const resumenBuzon = ({
  pasos,
  avisos,
  nuevas,
  hayAlerta,
  alcance = ALCANCE_COMPLETO,
}: DatosResumenBuzon): FragmentoResumen[] => {
  if (pasos.length === 0 && avisos === 0) {
    return [];
  }

  const f: FragmentoResumen[] = [];
  const normal = (texto: string) => f.push({ texto, tono: 'normal' });
  const fuerte = (texto: string) => f.push({ texto, tono: 'fuerte' });
  const urgente = (texto: string) => f.push({ texto, tono: 'urgente' });

  // Solo se cuentan pendientes: las que llegaron y ya se atendieron no están en la página.
  if (nuevas !== null && nuevas > 0) {
    const cuantas = plural(nuevas, 'notificación', 'notificaciones');
    normal(`Desde tu última visita ${nuevas === 1 ? 'te llegó' : 'te llegaron'} `);
    fuerte(alcance.nuevasExactas ? cuantas : `al menos ${cuantas}`);
    normal(nuevas === 1 ? ' que sigue pendiente. ' : ' que siguen pendientes. ');
  }

  const { cargadas, tareasTotales } = alcance;
  // En el panel puede quedar una sola mientras sale una fila descartada.
  const enLasRecientes = cargadas === 1 ? 'en tu notificación más reciente' : `entre tus ${cargadas} notificaciones más recientes`;

  if (pasos.length === 0) {
    if (!alcance.tareasCompletas) {
      normal(`No hay tareas ${enLasRecientes}`);
      if (tareasTotales !== null && tareasTotales > 0) {
        normal('; tienes ');
        fuerte(plural(tareasTotales, 'tarea', 'tareas'));
        normal(' en todo el buzón.');
      } else {
        normal('.');
      }
    } else if (alcance.parcial) {
      // El contador dice que no hay tareas: eso sí es exacto, pero los avisos que se ven no son todos.
      normal(
        avisos === 1
          ? 'No tienes tareas por hacer. Aquí ves tu aviso más reciente.'
          : `No tienes tareas por hacer. Aquí ves tus ${avisos} avisos más recientes.`
      );
    } else {
      normal(
        avisos === 1
          ? 'No tienes tareas por hacer. Queda 1 aviso que solo te informa.'
          : `No tienes tareas por hacer. Quedan ${avisos} avisos que solo te informan.`
      );
    }
    return f;
  }

  const enPagina = pasos.length;
  const atrasadas = pasos.filter((p) => p.atrasada).length;
  const decirAtraso = (union: string) => {
    if (atrasadas === 0) {
      return;
    }
    normal(union);
    if (atrasadas === enPagina) {
      urgente(enPagina === 1 ? 'lleva días esperando' : 'todas llevan días esperando');
    } else {
      urgente(`${atrasadas} ${atrasadas === 1 ? 'lleva' : 'llevan'} días esperando`);
    }
  };
  // Con tareas fuera de la página no se supone el orden de la página: la más reciente de todas podría
  // no estar aquí.
  const empiezaPorEntreLasVistas = enPagina === 1 ? '. Empieza por: ' : '. De estas, empieza por: ';

  if (alcance.tareasCompletas) {
    normal('Tienes ');
    fuerte(plural(enPagina, 'tarea', 'tareas'));
    decirAtraso(' y ');
    normal('. Empieza por: ');
  } else if (tareasTotales !== null && tareasTotales > enPagina) {
    normal('Tienes ');
    fuerte(plural(tareasTotales, 'tarea', 'tareas'));
    normal(enPagina === 1 ? '; aquí ves la más reciente' : `; aquí ves las ${enPagina} más recientes`);
    decirAtraso(', y ');
    normal(empiezaPorEntreLasVistas);
  } else {
    normal(`${mayusculaAlInicio(enLasRecientes)}: tienes `);
    fuerte(plural(enPagina, 'tarea', 'tareas'));
    decirAtraso(' y ');
    normal(empiezaPorEntreLasVistas);
  }

  const [primero] = pasos;
  fuerte(primero.frase);
  const mensaje = mensajeEnLinea(primero.notificacion.Mensaje);
  normal(mensaje ? ` (${mensaje}).` : '.');

  if (hayAlerta) {
    normal(' ');
    urgente('Antes, revisa la alerta: el servicio de conciliación notificó la cancelación de un CFDI.');
  }
  return f;
};

/** @function textoChipAtrasadas @description "1 atrasada", "3 atrasadas". */
export const textoChipAtrasadas = (n: number): string => plural(n, 'atrasada', 'atrasadas');

/** @function textoChipNuevas @description "1 nueva desde tu última visita". */
export const textoChipNuevas = (n: number): string => `${plural(n, 'nueva', 'nuevas')} desde tu última visita`;

/** @function textoAvisosEnEspera @description "2 avisos que no te piden nada". */
export const textoAvisosEnEspera = (n: number): string =>
  n === 1 ? '1 aviso que no te pide nada' : `${n} avisos que no te piden nada`;
